import { CdrBillingCategory } from '@reliance/types/enums/CdrBillingCategory';
import { Feature } from '@reliance/types/enums/Feature';
import { CdrFilters } from '@reliance/types/filters/CdrFilters';
import { Inmate } from '@reliance/types/models/Inmate';
import { InmateBooking } from '@reliance/types/models/InmateBooking';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useCallback, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import CdrsModal from '../../cdr/CdrsModal';
import { CdrTableColumnId } from '../../cdr/Table';
import { facilityHasFeature } from '../../facility/facilityFeatures';
import { formatCredentialName } from '../../format/ApiCredential';
import { formatDate } from '../../format/DateTime';
import { formatDuration } from '../../format/Duration';
import { useFacilityById } from '../../hooks/Facility';
import { useInmateBookingsByInmateId } from '../../hooks/InmateBooking';
import { useTabLazyLoad } from '../../hooks/useTabLazyLoad';
import { useNotifyTabLoading } from '../../hooks/useTabLoadingReporter';
import BaseHtmlTable from '../../table/BaseHtmlTable';

type InmateBookingsProps = {
  inmate: Inmate;
  tabSelected?: boolean;
  refreshTrigger?: number;
};

const BOOKING_CDR_COLUMNS: CdrTableColumnId[] = [
  'setupDate',
  'duration',
  'sourceIds',
  'destinations',
  'completion',
  'termination',
];

const InmateBookings: React.FC<InmateBookingsProps> = (props) => {
  const { inmate, tabSelected, refreshTrigger } = props;
  const { result: facility, loading: facilityLoading } = useFacilityById(
    inmate.facility?.id
  );
  const bookingCallSecondsRemaining = inmate.bookingCallSecondsRemaining ?? 0;
  const bookingCallsEnabled = facilityHasFeature(facility, Feature.BOOKING_CALLS);
  const [showBookingCdrs, setShowBookingCdrs] = useState(false);

  // Build the filters for the booking calls for the modal
  const bookingCdrFilters = useMemo(
    (): CdrFilters => ({
      inmateId: String(inmate.id),
      billingCategory: CdrBillingCategory.BOOKING_CALL,
      orderBy: '-setupDate',
      perPage: 500,
    }),
    [inmate.id]
  );

  const bookingsResult = useInmateBookingsByInmateId(null) as {
    result?: InmateBooking[];
    loading: boolean;
    execute: (id: number) => void;
  };

  useNotifyTabLoading('bookings', bookingsResult.loading);

  useTabLazyLoad({
    key: inmate.id,
    tabSelected,
    refreshTrigger,
    execute: useCallback(() => {
      if (inmate.id) {
        bookingsResult.execute(inmate.id);
      }
    }, [bookingsResult, inmate.id]),
  });

  const data = bookingsResult.result ?? [];
  const columnHelper = createColumnHelper<InmateBooking>();

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.dateBooked, {
        id: 'dateBooked',
        header: 'Date Booked',
        cell: (info) => formatDate(info.getValue()),
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.dateReleased, {
        id: 'dateReleased',
        header: 'Date Released',
        cell: (info) => formatDate(info.getValue()),
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.bookedBy, {
        id: 'bookedBy',
        header: 'Booked By',
        cell: (info) => info.getValue() ?? '',
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.bookedByCredential, {
        id: 'bookedByCredential',
        header: 'Booked By User',
        cell: (info) => formatCredentialName(info.getValue()),
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.releasedBy, {
        id: 'releasedBy',
        header: 'Released By',
        cell: (info) => info.getValue() ?? '',
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.releasedByCredential, {
        id: 'releasedByCredential',
        header: 'Released By User',
        cell: (info) => formatCredentialName(info.getValue()),
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.bookingCount, {
        id: 'bookingCount',
        header: 'Count',
        cell: (info) => info.getValue() ?? '',
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.bookingId, {
        id: 'bookingId',
        header: 'Booking ID',
        cell: (info) => info.getValue() ?? '',
        enableColumnFilter: false,
      }),
    ],
    [columnHelper]
  );

  return (
    <div className="jarviswidget">
      <div className="content">
        {!facilityLoading && facility != null && !bookingCallsEnabled && (
          <Alert variant="danger" className="py-2 px-3 mb-3">
            Booking calls are disabled for this facility.
          </Alert>
        )}
        <Alert
          variant="secondary"
          className="d-flex align-items-center justify-content-between flex-wrap gap-2 py-2 px-3 mb-3">
          <div>
            <span className="text-muted">Booking call time remaining</span>
            {': '}
            <span className={bookingCallSecondsRemaining <= 0 ? 'text-muted' : undefined}>
              {formatDuration(bookingCallSecondsRemaining)}
            </span>
          </div>
          <Button size="sm" variant="outline-secondary" onClick={() => setShowBookingCdrs(true)}>
            View Booking Call(s)
          </Button>
        </Alert>

        <div className="row">
          <div className="col-md-12">
            <BaseHtmlTable
              rows={data}
              columnDefs={columns}
              paginationMode="client"
              perPage={50}
              loading={bookingsResult.loading}
              pageSizeOptions={[25, 50, 100]}
            />
          </div>
        </div>

        <CdrsModal
          show={showBookingCdrs}
          title="Booking Calls"
          filters={bookingCdrFilters}
          visibleColumns={BOOKING_CDR_COLUMNS}
          onHide={() => setShowBookingCdrs(false)}
        />
      </div>
    </div>
  );
};

export default InmateBookings;
